import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Backdrop from '@mui/material/Backdrop';
import Slide from '@mui/material/Slide';
import FocusTrap from '@mui/base/FocusTrap';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import { useFeatureFlag } from 'modules/common/hooks';
import { AriaAlert } from 'modules/common/components/Aria/AriaAlert';
import { useOfflineState } from './useOffline';

interface OfflineScreenStyleProps {
  isOnline: boolean;
}

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: theme.spacing(110),
    minHeight: theme.spacing(11),
    padding: theme.spacing(0, 2),
    margin: theme.spacing(2),
    backgroundColor: ({ isOnline }: OfflineScreenStyleProps) =>
      isOnline ? theme.palette.success.light : theme.palette.error.light
  },
  cardContent: {
    display: 'flex'
  },
  description: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4)
  },
  coloredIcon: {
    color: ({ isOnline }: OfflineScreenStyleProps) =>
      isOnline ? theme.palette.success.dark : theme.palette.error.dark
  },
  coloredText: {
    color: ({ isOnline }: OfflineScreenStyleProps) =>
      isOnline ? theme.palette.success.dark : theme.palette.error.dark
  },
  successIcon: {
    marginRight: theme.spacing(4),
    color: theme.palette.success.main
  },
  backdrop: {
    display: 'flex',
    zIndex: 10000,
    justifyContent: 'flex-end',
    flexDirection: 'column',
    paddingBottom: theme.spacing(10)
  },
  root: {
    position: 'absolute',
    zIndex: 10000,
    bottom: theme.spacing(10),
    width: '90%',
    left: 0,
    right: 0,
    margin: 'auto',
    maxWidth: theme.spacing(110),
    overflow: 'hidden'
  }
}));

const OfflineScreen = ({
  showBackdrop = true
}: {
  showBackdrop?: boolean;
}): ReactElement => {
  const isOnline = useOfflineState();
  const classes = useStyles({ isOnline });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialMount = useRef(false);

  const [offlineToOnline, setOfflineToOnline] = useState(false);

  // only run when going offline to online
  useEffect(() => {
    if (initialMount.current) {
      if (isOnline) {
        setOfflineToOnline(true);
      }
    } else {
      initialMount.current = true;
    }
  }, [isOnline]);

  // connection re-gained message timeout
  useEffect(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setOfflineToOnline(false);
    }, 2000);
  }, [offlineToOnline]);

  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const children = useMemo(() => {
    return (
      <Card className={classes.cardRoot}>
        <CardContent className={classes.cardContent}>
          <WifiOffIcon className={classes.coloredIcon} fontSize="large" />
          <div className={classes.description}>
            <Typography className={classes.coloredText} variant="h6">
              <FormattedMessage id="offlineScreen.offline.message" />
            </Typography>
            <Typography>
              <FormattedMessage id="offlineScreen.offline.description" />
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }, [classes]);

  return (
    <div data-testid="offline-screen" className={classes.root}>
      {/* Backdrop has issue with screen reader https://github.com/mui/material-ui/issues/31371 */}
      {isTiwScreenReaderEnabled &&
        (offlineToOnline && isOnline ? (
          <AriaAlert>
            <FormattedMessage id="offlineScreen.online.message" />
          </AriaAlert>
        ) : !isOnline ? (
          <AriaAlert>
            <FormattedMessage id="offlineScreen.offline.message" />
          </AriaAlert>
        ) : (
          <></>
        ))}

      <Slide
        data-testid="online-toast"
        direction="up"
        in={offlineToOnline && isOnline}
        mountOnEnter
        unmountOnExit
      >
        <Card className={classes.cardRoot}>
          <CardContent className={classes.cardContent}>
            <WifiIcon className={classes.coloredIcon} fontSize="large" />
            <div className={classes.description}>
              <Typography className={classes.coloredText} variant="h6">
                <FormattedMessage id="offlineScreen.online.message" />
              </Typography>
            </div>
          </CardContent>
          <CheckCircleIcon className={classes.successIcon} fontSize="large" />
        </Card>
      </Slide>
      <Backdrop
        data-testid="offline-toast"
        className={classes.backdrop}
        open={!isOnline}
        mountOnEnter
        unmountOnExit
        invisible={!showBackdrop}
      >
        {isTiwVpatEnabled ? (
          <FocusTrap open={!isOnline}>{children}</FocusTrap>
        ) : (
          <>{children}</>
        )}
      </Backdrop>
    </div>
  );
};

export default OfflineScreen;
